// ChairpersonArrears.jsx
import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TableContainer,
  Chip,
  IconButton,
  Tooltip,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { motion } from "framer-motion";
import Swal from "sweetalert2";
import { fetchMembers } from "../services/memberService";
import {
  fetchArrearsInvoices,
} from "../services/arrearsService";
import { useTranslation } from "react-i18next";

// ---- THEME
const brand = "#800b33";
const brandHover = "#6c0a2b";

// ---- UTILITIES
const tzs = (n) =>
  Number.isFinite(Number(n))
    ? new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(Number(n))
    : n ?? "0";

const fmtDate = (d) => {
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return d;
  return dt.toISOString().slice(0, 10);
};

const exportCSV = (filename, rows, columns) => {
  const header = columns.map((c) => `"${c.header}"`).join(",");
  const body = rows
    .map((r) =>
      columns
        .map((c) => {
          const v =
            typeof c.accessor === "function" ? c.accessor(r) : r[c.accessor];
          return `"${String(v ?? "").replace(/"/g, '""')}"`;
        })
        .join(",")
    )
    .join("\n");
  const blob = new Blob([header + "\n" + body], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const STATUS_COLOR = {
  ACTIVE: "success",
  SUSPENDED: "warning",
  PENDING: "warning",
  PAID: "success",
  FAILED: "error",
};

// ---- ANIMATIONS
const fadeIn = {
  hidden: { opacity: 0, y: 18 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.35, ease: "easeOut" },
  }),
};

// ---- MAIN
export default function ChairpersonArrears() {
  const { t } = useTranslation();
  // API Data State
  const [members, setMembers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  // Left table (Members)
  const [qMembers, setQMembers] = useState("");
  const [statusMembers, setStatusMembers] = useState("all");
  const [pageMembers, setPageMembers] = useState(0);
  const [rppMembers, setRppMembers] = useState(10);

  // Right table (Invoices)
  const [statusInvoices, setStatusInvoices] = useState("all");
  const [qInvoices, setQInvoices] = useState("");
  const [pageInv, setPageInv] = useState(0);
  const [rppInv, setRppInv] = useState(10);

  // View Invoice Modal
  const [view, setView] = useState(null);

  // ---- DATA FETCHING
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [membersData, invoicesData] = await Promise.all([
        fetchMembers(),
        fetchArrearsInvoices(),
      ]);
      setMembers(membersData);
      setInvoices(invoicesData);
    } catch (err) {
      setError(err.message);
      Swal.fire({
        icon: "error",
        title: t("Failed to load data"),
        text: err.message,
        confirmButtonColor: brand,
      });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { loadData(); }, [loadData]);

  // ---- FILTERED (MEMBERS)
  const membersFiltered = useMemo(() => {
    const q = qMembers.trim().toLowerCase();
    return members.filter(m => {
      // Corrected status filtering logic
      if (statusMembers !== "all") {
        const isActive = statusMembers === "ACTIVE";
        if (m.is_active !== isActive) {
          return false;
        }
      }

      if (q) {
        const hay = `${m.user.full_name} ${m.user.phone_number}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [members, qMembers, statusMembers]);

  const membersPaginated = useMemo(() => {
    if (rppMembers === -1) return membersFiltered;
    const start = pageMembers * rppMembers;
    return membersFiltered.slice(start, start + rppMembers);
  }, [membersFiltered, pageMembers, rppMembers]);

  // ---- FILTERED (INVOICES)
  const invoicesFiltered = useMemo(() => {
    const q = qInvoices.trim().toLowerCase();
    return invoices.filter((inv) => {
      if (statusInvoices !== "all" && inv.status !== statusInvoices) return false;
      if (q) {
        const hay = `${inv.invoice_id} ${inv.member_details?.user?.full_name || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [invoices, statusInvoices, qInvoices]);

  const invoicesPaginated = useMemo(() => {
    if (rppInv === -1) return invoicesFiltered;
    const start = pageInv * rppInv;
    return invoicesFiltered.slice(start, start + rppInv);
  }, [invoicesFiltered, pageInv, rppInv]);

  // ---- ACTIONS
  const resetMembersFilters = () => {
    setQMembers("");
    setStatusMembers("all");
    setPageMembers(0);
  };

  const resetInvoicesFilters = () => {
    setQInvoices("");
    setStatusInvoices("all");
    setPageInv(0);
  };

  const onExportMembers = () =>
    exportCSV("group_members.csv", membersFiltered, [
      { header: t("Member"), accessor: (r) => r.user.full_name },
      { header: t("Phone"), accessor: (r) => r.user.phone_number },
      { header: t("Status"), accessor: "status" },
      { header: t("Last Paid"), accessor: (r) => fmtDate(r.last_contribution_date) },
      { header: t("Missed Periods"), accessor: "missed_periods" },
    ]);

  const onExportInvoices = () =>
    exportCSV("arrears_invoices.csv", invoicesFiltered, [
      { header: t("Invoice ID"), accessor: "invoice_id" },
      { header: t("Member"), accessor: (r) => r.member_details?.user?.full_name },
      { header: t("Status"), accessor: "status" },
      { header: t("Missed"), accessor: "missed_periods" },
      { header: t("Current Total Due (TZS)"), accessor: (r) => tzs(r.current_total_due) },
      { header: t("Created"), accessor: (r) => new Date(r.created_at).toLocaleString() },
    ]);

  // ---- RENDER
  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 900, color: brand }}>
          {t("Arrears Management")}
        </Typography>
        <Typography variant="subtitle2" color="text.secondary">
          {t("Generate arrears invoices from members list, and track invoice lifecycle.")}
        </Typography>
      </Box>

      <Grid container spacing={3}>
      

        {/* RIGHT: INVOICES TABLE */}
        <Grid item xs={12} lg={12}>
          <motion.div variants={fadeIn} initial="hidden" animate="visible">
            <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 2 }}>
              <CardContent sx={{ pb: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: brand, mb: 1 }}>
                  {t("Arrears Invoices")}
                </Typography>

                {/* Filters */}
                <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
                  <Grid item xs={12} md={7}>
                    <TextField
                      fullWidth
                      placeholder={t("Search invoice no. or member")}
                      value={qInvoices}
                      onChange={(e) => {
                        setQInvoices(e.target.value);
                        setPageInv(0);
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={8} md={3.5}>
                    <FormControl fullWidth>
                      <InputLabel id="arrears-status-label">{t("Status")}</InputLabel>
                      <Select
                        labelId="arrears-status-label"
                        label={t("Status")}
                        value={statusInvoices}
                        onChange={(e) => {
                          setStatusInvoices(e.target.value);
                          setPageInv(0);
                        }}
                      >
                        <MenuItem value="all">{t("All")}</MenuItem>
                        <MenuItem value="PENDING">{t("PENDING")}</MenuItem>
                        <MenuItem value="PAID">{t("PAID")}</MenuItem>
                        <MenuItem value="FAILED">{t("FAILED")}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={4} md={4} sx={{ display: "flex",  justifyContent: "flex-start", alignItems: "center" }}>
                    <Tooltip title={t("Reset filters")}>
                      <IconButton onClick={resetInvoicesFilters}>
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                    <Button
                      variant="contained"
                      startIcon={<FileDownloadIcon />}
                      onClick={onExportInvoices}
                      sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }}
                    >
                      {t("Export")}
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>

              {loading && <Typography sx={{ textAlign: 'center', p: 2 }}>{t("Loading invoices...")}</Typography>}
              {error && !loading && (
                <Typography color="error" sx={{ textAlign: 'center', p: 2 }}>
                  {t("Error: {{message}}", { message: error })}
                </Typography>
              )}
              <CardContent sx={{ p: 0 }}>
                <TableContainer component={Paper} elevation={0}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{t("Invoice")}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{t("Member")}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{t("Status")}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">
                          {t("Missed")}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">
                          {t("Current Total Due (TZS)")}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{t("Created")}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="center">
                          {t("Actions")}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoicesPaginated.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 6, color: "text.secondary" }}>
                            {t("No invoices match your filters.")}
                          </TableCell>
                        </TableRow>
                      ) : (
                        invoicesPaginated.map((inv, idx) => (
                          <TableRow key={inv.invoice_id} hover>
                            <TableCell>
                              {rppInv === -1 ? idx + 1 : pageInv * rppInv + idx + 1}
                            </TableCell>
                            <TableCell>{inv?.invoice_id}</TableCell>
                            <TableCell>{inv?.member_details?.user?.full_name}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={t(inv?.status)}
                                color={STATUS_COLOR[inv?.status]}
                                sx={{ fontWeight: 600 }}
                              />
                            </TableCell>
                            <TableCell align="right">{inv?.missed_periods}</TableCell>
                            <TableCell align="right">{tzs(inv?.current_total_due)}</TableCell>
                            <TableCell>{new Date(inv?.created_at).toLocaleString()}</TableCell>
                            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                              <Tooltip title={t("View")}>
                                <IconButton onClick={() => setView(inv)}>
                                  <VisibilityIcon />
                                </IconButton>
                              </Tooltip>
                             
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Divider />
                <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2 }}>
                  <TablePagination
                    component="div"
                    count={invoicesFiltered.length}
                    page={pageInv}
                    onPageChange={(_, p) => setPageInv(p)}
                    rowsPerPage={rppInv === -1 ? invoicesFiltered.length : rppInv}
                    onRowsPerPageChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      setRppInv(v);
                      setPageInv(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100, { label: t("All"), value: -1 }]}
                  />
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      {/* VIEW INVOICE MODAL */}
      <Dialog open={!!view} onClose={() => setView(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900, color: brand }}>{t("Invoice Details")}</DialogTitle>
        <DialogContent dividers sx={{ overflowWrap: "anywhere" }}>
          {view && (
            <>
              <Typography><b>{t("Invoice ID:")}</b> {view?.invoice_id}</Typography>
              <Typography>
                <b>{t("Member:")}</b> {view?.member_details?.user?.full_name}({view?.member_details?.user?.phone_number})
              </Typography>
              <Typography><b>{t("Status:")}</b> {t(view?.status)}</Typography>
              <Typography><b>{t("Missed Periods:")}</b> {view?.missed_periods}</Typography>
              <Typography><b>{t("Current Total Due")}: </b>{tzs(view.current_total_due)} TZS</Typography>
              <Typography><b>{t("Outstanding Base")}: </b>{tzs(view.outstanding_base_amount)} TZS</Typography>
              <Typography><b>{t("Current SSC")}: </b>{tzs(view.current_ssc_amount)} TZS ({view.current_ssc_rate_percent}%)</Typography>
              <Typography><b>{t("Current Bank Charge")}: </b>{tzs(view.current_bank_charge_amount)} TZS ({view.current_bank_charge_rate_percent}%)</Typography>
              <Typography><b>{t("Base Allocated")}: </b>{tzs(view.amount_paid)} TZS</Typography>
              <Divider sx={{ my: 2 }} />
              <Typography><b>{t("Original Base")}: </b>{tzs(view.base_amount)} TZS</Typography>
              <Typography><b>{t("Original SSC")}: </b>{tzs(view.original_ssc_amount)} TZS</Typography>
              <Typography><b>{t("Original Total")}: </b>{tzs(view.original_total_amount)} TZS</Typography>
              <Typography><b>{t("Created:")}</b> {new Date(view?.created_at).toLocaleString()}</Typography>

              <Divider sx={{ my: 2 }} />

              <Typography sx={{ fontWeight: 700, mb: 1 }}>{t("Audit Trail")}</Typography>
              {/* You may need to add an audit trail to your backend model if you want this feature */}
              <Paper variant="outlined" sx={{ p: 1.25, bgcolor: 'grey.50' }}>
                <Typography variant="body2">
                  {t("Created by Chairperson at {{date}}", { date: new Date(view.created_at).toLocaleString() })}
                </Typography>
              </Paper>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setView(null)}>{t("Close")}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
